import React, { createContext, useContext, useState, useRef } from "react";
import {
  ERROR,
  LOADING,
  SUCCESS,
  colleagues,
  connections,
} from "../constants/common";
import {
  connectUser,
  deleteConnection,
  getNetworkConnections,
  getNetwork,
  getOtherConnections,
} from "../services/network.services";
import { useAlert } from "./Alert";
import { CometChat } from "@cometchat/chat-sdk-javascript";

const NetworkContext = createContext({});

export const NetworkContextProvider = ({ children }) => {
  const { sendAlert } = useAlert();
  const abortControllerRef = useRef(null);
  const [networkState, setNetworkState] = useState({
    fetchStatus: "",
    connectStatus: "",
    network: {
      data: [],
    },
    fitlersData: { page: 1 },
  });

  const handleState = (obj) => {
    setNetworkState((prevState) => {
      const newData = {
        ...prevState,
        ...obj,
      };

      return {
        ...prevState,
        ...obj,
      };
    });
  };

  const fetchNetwork = async (params, type, withoutLoader, isProfile) => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    if (!withoutLoader) handleState({ fetchStatus: LOADING });

    try {
      handleState({ network: { data: [] } });

      let response;
      if (!type || type === connections) {
        if (isProfile) {
          response = await getNetworkConnections(params, signal);
        } else {
          response = await getNetwork({ ...params, user_types: ["first"] }, signal);
        }
      }

      if (type === colleagues) response = await getNetwork(params, signal);

      handleState({
        network: response?.data?.data || { data: [] },
        fetchStatus: SUCCESS,
      });
    } catch (error) {
      if (error.name !== "AbortError") {
        sendAlert(error.response?.data?.message, "error");
        handleState({ fetchStatus: ERROR, network: { data: [] } });
        return Promise.reject(error);
      }
    }
  };

  const fetchOthersNetwork = async (params) => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    try {
      handleState({ fetchStatus: LOADING });
      handleState({ network: { data: [] } });

      const response = await getOtherConnections(params, signal);
      handleState({
        network: response?.data?.data || { data: [] },
        fetchStatus: SUCCESS,
      });
    } catch (error) {
      if (error.name !== "AbortError") {
        sendAlert(error.response?.data?.message, "error");
        handleState({ fetchStatus: ERROR, network: { data: [] } });
        return Promise.reject(error);
      }
    }
  };

  const connectWithOthers = async (data) => {
    handleState({ connectStatus: LOADING });
    try {
      const response = await connectUser(data);

      // Tag conversation in CometChat as "Trusted"
      const tags = ["Trusted"];
      CometChat.tagConversation(
        String(data.connected_user_id),
        "user",
        tags
      ).then(
        (conversation) => {
          console.log("Tagged conversation as Trusted:", conversation);
        },
        (error) => {
          console.error("Error tagging conversation:", error);
        }
      );

      handleState({
        connectStatus: SUCCESS,
      });
      sendAlert("Connected successfully!", "success");
      return response;
    } catch (error) {
      handleState({ connectStatus: ERROR });
      sendAlert(error.response?.data?.message, "error");
      Promise.reject(error);
    }
  };

  const disconnectWithOthers = async (data) => {
    handleState({ connectStatus: LOADING });
    try {
      const response = await deleteConnection(data);

      // Remove "Trusted" tag from conversation in CometChat
      const tags = [];
      CometChat.tagConversation(
        String(data.id),
        "user",
        tags
      ).then(
        (conversation) => {
          console.log("Removed Trusted tag from conversation:", conversation);
        },
        (error) => {
          console.error("Error removing tag from conversation:", error);
        }
      );

      handleState({
        connectStatus: SUCCESS,
      });
      sendAlert("Connection deleted successfully!", "success");
      return response;
    } catch (error) {
      handleState({ connectStatus: ERROR });
      sendAlert(error.response?.data?.message, "error");
      Promise.reject(error);
    }
  };

  const updateNetworkState = (field, value) => {
    handleState({ [field]: value });
  };

  const setFitlersData = (data) => {
    handleState({ fitlers: { ...networkState.fitlers, ...data } });
  };

  const state = {
    ...networkState,
    disconnectWithOthers,
    fetchNetwork,
    fetchOthersNetwork,
    connectWithOthers,
    updateNetworkState,
    setFitlersData,
  };

  return (
    <NetworkContext.Provider value={state}>{children}</NetworkContext.Provider>
  );
};

export const useNetwork = () => useContext(NetworkContext);
